import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { useInterfaceMode } from "@/auth/InterfaceModeContext";
import { needsTradingAccount } from "@/auth/accountScope";
import { workspaceForLocation, workspaceMountKey, navigationForWorkspace } from "@/auth/workspace";
import { isModuleEnabled } from "@/auth/modules";
import { NAV_GROUPS, type NavGroup, type NavItem } from "@/auth/nav";
import AccountSwitcher from "@/components/AccountSwitcher";
import CommandPalette from "@/components/CommandPalette";
import DataAndonBanner from "@/components/DataAndonBanner";
import ModuleQuickToggle from "@/components/ModuleQuickToggle";
import HelpButton from "@/help/HelpButton";
import PageGuide from "@/help/PageGuide";
import { isAppReachable } from "@/lib/connectivity";
import FeatureUsageTracker from "@/components/FeatureUsageTracker";
import FrequentTools from "@/components/FrequentTools";

// The collapsed copilot still pulls in the full Copilot page and its render helpers.
// Keep it out of the login/initial bundle and load it only after the authenticated shell mounts.
const FloatingCopilot = lazy(() => import("@/components/FloatingCopilot"));

export default function App() {
  const { me, tradingEnvironment, currentAccount } = useAuth();
  const { mode, setMode } = useInterfaceMode();
  const location = useLocation();
  const workspace = workspaceForLocation(location.pathname, location.search);
  const tradingWorkspace = workspace === "trading";
  const missingAccount = !currentAccount && needsTradingAccount(location.pathname, location.search);
  // 按 module 过滤每个 group 内的 items；过滤后整组为空就隐藏
  const visibleGroups = useMemo<NavGroup[]>(() => {
    const groups = NAV_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => isModuleEnabled(me?.preferences, it.module)),
      }))
      .filter((g) => g.items.length > 0);
    return navigationForWorkspace(groups, workspace);
  }, [me?.preferences, workspace]);
  const researchEntry = NAV_GROUPS.find(group => group.id === "research-workspace")?.items.find(item => isModuleEnabled(me?.preferences, item.module))?.to;
  const tradingEntry = NAV_GROUPS.find(group => group.id === "trading-workspace")?.items.find(item => isModuleEnabled(me?.preferences, item.module))?.to;

  const [offline, setOffline] = useState(false);
  const [staleAt, setStaleAt] = useState<number | null>(null);
  const [copilotLoaded, setCopilotLoaded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("whaleseek_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => setSidebarCollapsed((value) => {
    const next = !value;
    try { localStorage.setItem("whaleseek_sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });
  const connectivityFailures = useRef(0);

  useEffect(() => {
    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const probe = async () => {
      const reachable = await isAppReachable();
      if (disposed) return;
      if (reachable) {
        connectivityFailures.current = 0;
        setOffline(false);
        return;
      }

      connectivityFailures.current += 1;
      if (connectivityFailures.current >= 2) {
        setOffline(true);
      } else {
        retryTimer = setTimeout(() => void probe(), 2000);
      }
    };

    const on = () => void probe();
    // navigator.onLine is only a hint. Confirm against our own origin before
    // showing an offline banner, which avoids false alarms on VPNs/adapters.
    const off = () => {
      connectivityFailures.current = Math.max(connectivityFailures.current, 1);
      void probe();
    };
    const visible = () => {
      if (document.visibilityState === "visible") void probe();
    };
    const stale = () => setStaleAt(Date.now());
    void probe();
    const interval = window.setInterval(() => void probe(), 30000);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    document.addEventListener("visibilitychange", visible);
    document.addEventListener("whaleseek:stale", stale as EventListener);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      document.removeEventListener("visibilitychange", visible);
      document.removeEventListener("whaleseek:stale", stale as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!staleAt || offline) return;
    const t = setTimeout(() => setStaleAt(null), 5000);
    return () => clearTimeout(t);
  }, [staleAt, offline]);

  return (
    <div className={`min-h-screen flex flex-col border-t-4 ${tradingWorkspace ? tradingEnvironment === "live" ? "border-red-600" : "border-emerald-500" : "border-sky-500"}`}>
      <FeatureUsageTracker enabled={!missingAccount} />
      {me && tradingWorkspace && tradingEnvironment === "live" && (
        <div className="bg-red-600 px-3 py-1 text-center text-xs font-semibold text-white" role="alert">
          🔴 当前为实盘环境 · {currentAccount?.name ?? "尚未选择真实账户"} · 交易操作将进入真实账户
        </div>
      )}
      {(offline || staleAt) && (
        <div className={`text-xs text-center py-1 ${offline ? "bg-amber-500 text-white" : "bg-zinc-700 text-zinc-100"}`}>
          {offline
            ? "📡 离线模式 — 显示的是缓存数据，实时性可能滞后"
            : "📡 已恢复网络，部分数据正在刷新"}
        </div>
      )}
      <header className="border-b bg-white/80 dark:bg-zinc-900/80 backdrop-blur sticky top-0 z-20">
        <div className="min-h-14 w-full px-3 py-2 flex flex-wrap items-center gap-3">
          <Link to="/" className="font-semibold text-lg tracking-wide shrink-0 xl:w-[13rem]">
            🐋 <span className="hidden sm:inline">WhaleSeek</span> <span className="hidden font-medium text-sky-600 md:inline dark:text-sky-400">寻鲸</span>
          </Link>
          <nav aria-label="工作区切换" className="order-last flex w-full gap-1 rounded-lg bg-zinc-100 p-1 text-xs dark:bg-zinc-800 sm:order-none sm:w-auto">
            {researchEntry && <Link to={researchEntry} aria-current={workspace === "research" ? "page" : undefined} className={`rounded-md px-3 py-1.5 ${workspace === "research" ? "bg-white font-semibold text-blue-700 shadow-sm dark:bg-zinc-950 dark:text-blue-300" : "text-zinc-500"}`}>研究</Link>}
            {tradingEntry && <Link to={tradingEntry} aria-current={tradingWorkspace ? "page" : undefined} className={`rounded-md px-3 py-1.5 ${tradingWorkspace ? "bg-white font-semibold text-blue-700 shadow-sm dark:bg-zinc-950 dark:text-blue-300" : "text-zinc-500"}`}>交易</Link>}
            <Link to="/library" aria-current={location.pathname.startsWith("/library") || workspace === "publishing" ? "page" : undefined} className="rounded-md px-3 py-1.5 text-zinc-500">策略与因子库</Link>
          </nav>
          <details className="xl:hidden text-sm" onClick={event => { if ((event.target as Element).closest("a")) event.currentTarget.removeAttribute("open"); }}>
            <summary className="cursor-pointer whitespace-nowrap">菜单</summary>
            <div className="fixed left-3 right-3 top-14 max-h-[70vh] overflow-y-auto rounded border bg-white dark:bg-zinc-900 shadow-lg p-3">
              {visibleGroups.map(group => <div key={group.id} className="mb-3"><div className="text-xs text-zinc-500 mb-1">{group.label}</div><div className="flex flex-wrap gap-1">{group.items.map(item => <NavSubLink key={item.to} item={item} />)}</div></div>)}
            </div>
          </details>
          <div className="min-w-0 max-w-full flex flex-wrap items-center justify-end gap-2 ml-auto">
            {me && <DataAndonBanner />}
            {me && <button
              type="button"
              onClick={() => setMode(mode === "professional" ? "guided" : "professional")}
              className="rounded-md border px-2.5 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              title={mode === "professional" ? "当前为专业视图；点击显示页面教学与步骤说明" : "当前为引导视图；点击隐藏教学内容并提高信息密度"}
            >
              {mode === "professional" ? "专业视图" : "引导视图"}
            </button>}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("whaleseek:open-cmdk"))}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
              title="搜索菜单 / 股票（Ctrl+K）"
            >
              🔍<span className="hidden lg:inline">搜索</span>
              <kbd className="hidden lg:inline px-1 py-0.5 text-[10px] bg-white dark:bg-zinc-900 rounded border dark:border-zinc-700">⌘K</kbd>
            </button>
            <ModuleQuickToggle />
            <AccountSwitcher showTradingContext={tradingWorkspace} />
          </div>
        </div>
      </header>
      <div className="flex flex-1 min-h-0">
        <DesktopSidebar groups={visibleGroups} collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <main className="min-w-0 flex-1 p-3 sm:p-6">
          {workspace === "research" && <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-200" aria-label="研究工作区">
            <span><b>研究工作区</b> · 历史回溯、方案对照与前向观察</span>
            <Link to="/quant-agents" className="underline">前往交易区管理执行 →</Link>
          </div>}
          <PageGuide />
          {missingAccount ? <section className="rounded border p-6">
            <p>当前{tradingEnvironment === "paper" ? "模拟" : "实盘"}环境没有可用账户。</p>
            <Link to={tradingEnvironment === "paper" ? "/settings/accounts?paper=1" : "/settings/accounts"} className="text-blue-600">创建或连接账户</Link>
          </section> : <Outlet key={workspaceMountKey(me?.id, workspace, tradingEnvironment, currentAccount?.id)} />}
        </main>
      </div>
      <footer className="border-t py-4 text-center text-xs text-zinc-400">
        WhaleSeek 寻鲸 · 自用 · {new Date().getFullYear()} · 任何页面 <kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded text-[10px]">⌘K</kbd> 搜索 · <kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded text-[10px]">❓</kbd> 帮助 · <a href="/reset.html" className="hover:text-blue-500 underline decoration-dotted" title="部署后看到旧版/加载出错时，一键注销 SW+清缓存">🔄 清缓存</a>
      </footer>
      <CommandPalette />
      <HelpButton />
      {copilotLoaded && me ? (
        <Suspense fallback={null}>
          <FloatingCopilot key={workspaceMountKey(me.id, workspace, tradingEnvironment, currentAccount?.id)} initiallyOpen />
        </Suspense>
      ) : me ? (
        <button
          type="button"
          onClick={() => setCopilotLoaded(true)}
          className="fixed bottom-5 right-20 w-12 h-12 rounded-full bg-violet-600 text-white text-xl shadow-lg hover:bg-violet-700 hover:shadow-xl transition z-40 flex items-center justify-center"
          title="AI Copilot — 选股 / 分析 / 对话"
          aria-label="打开 Copilot"
        >
          🤖
        </button>
      ) : null}
    </div>
  );
}

function DesktopSidebar({ groups, collapsed, onToggle }: { groups: NavGroup[]; collapsed: boolean; onToggle: () => void }) {
  const location = useLocation();
  const [menuQuery, setMenuQuery] = useState("");
  const normalizedQuery = menuQuery.trim().toLocaleLowerCase();
  const filteredGroups = normalizedQuery ? groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => `${group.label} ${item.label}`.toLocaleLowerCase().includes(normalizedQuery)),
  })).filter((group) => group.items.length > 0) : groups;
  return <aside className={`sticky top-14 hidden h-[calc(100vh-3.5rem)] shrink-0 overflow-y-auto border-r bg-white/90 transition-[width] dark:border-zinc-800 dark:bg-zinc-900/90 xl:block ${collapsed ? "w-14" : "w-56"}`}>
    <div className="sticky top-0 z-10 flex justify-end border-b bg-white/95 p-2 dark:border-zinc-800 dark:bg-zinc-900/95">
      <button type="button" onClick={onToggle} className="rounded px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" title={collapsed ? "展开导航" : "收起导航"} aria-label={collapsed ? "展开导航" : "收起导航"}>
        {collapsed ? "»" : "« 收起"}
      </button>
    </div>
    {!collapsed && <div className="sticky top-[41px] z-10 border-b bg-white/95 p-2 dark:border-zinc-800 dark:bg-zinc-900/95">
      <label className="relative block">
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400">⌕</span>
        <input value={menuQuery} onChange={(event) => setMenuQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Escape") setMenuQuery(""); }} placeholder="搜索菜单" aria-label="搜索左侧菜单" className="w-full rounded-md border bg-zinc-50 py-1.5 pl-7 pr-7 text-xs outline-none focus:border-blue-400 dark:border-zinc-700 dark:bg-zinc-950" />
        {menuQuery && <button type="button" onClick={() => setMenuQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400" aria-label="清空菜单搜索">×</button>}
      </label>
    </div>}
    <nav aria-label="主导航" className="space-y-3 p-2">
      {filteredGroups.map((group) => {
        const active = group.items.some((item) => location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to + "/")));
        if (collapsed) return <Link key={group.id} to={group.items[0]!.to} title={group.label} className={`flex h-9 items-center justify-center rounded text-lg ${active ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" : "hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}>{group.label.split(" ")[0]}</Link>;
        return <section key={group.id}>
          <div className={`px-2 pb-1 text-xs font-medium ${active ? "text-blue-700 dark:text-blue-300" : "text-zinc-400"}`}>{group.label}</div>
          <div className="space-y-0.5">{group.items.map((item) => <NavSubLink key={item.to} item={item} />)}</div>
        </section>;
      })}
      {!collapsed && filteredGroups.length === 0 && <div className="px-2 py-6 text-center text-xs text-zinc-400">没有匹配的菜单</div>}
    </nav>
    {!collapsed && <FrequentTools variant="sidebar" />}
  </aside>;
}

function NavGroupBtn({ group }: { group: NavGroup }) {
  const location = useLocation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // 路由切换后自动关
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // 点击外面关
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const isSingle = group.items.length === 1;
  const onlyItem = isSingle ? group.items[0]! : null;

  // 当前路径是否落在本组内（高亮）
  const matchedInGroup = group.items.some(
    (it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/"),
  );

  const baseCls = "px-2.5 py-1 rounded text-sm transition-colors hover:text-zinc-900 dark:hover:text-white whitespace-nowrap";
  const activeCls = matchedInGroup ? "text-zinc-900 dark:text-white font-medium" : "";

  // 单 item 组：直接渲染为 Link
  if (isSingle && onlyItem) {
    return (
      <Link to={onlyItem.to} className={`${baseCls} ${activeCls}`}>
        {group.label}
      </Link>
    );
  }

  // 多 item group：一级是 Link（点击直接跳第一个子项）+ 旁边一个 ▾ 按钮专门展开 dropdown
  // 即使 dropdown 因为父容器问题被截掉看不见，一级 link 也能保证跳转可用
  const firstItem = group.items[0]!;
  return (
    <div ref={wrapRef} className="relative inline-flex items-center">
      <Link
        to={firstItem.to}
        className={`${baseCls} ${activeCls} pr-1`}
        title={`默认进 ${firstItem.label}`}
      >
        {group.label}
      </Link>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`${baseCls} px-1 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${group.label} 子菜单`}
      >
        <span className="text-[10px]">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-48 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg z-30 py-1"
          role="menu"
        >
          {group.items.map((it) => <NavSubLink key={it.to} item={it} />)}
        </div>
      )}
    </div>
  );
}

// 当前路由所在 group 的兄弟链接条；single-item group / 找不到 group 则不渲染
function SecondaryNav({ groups }: { groups: NavGroup[] }) {
  const location = useLocation();

  // 在所有 group 的所有 item 里找"最长前缀匹配"，再回到所属 group
  let bestItem: NavItem | null = null;
  let bestGroup: NavGroup | null = null;
  for (const g of groups) {
    for (const it of g.items) {
      const matches = location.pathname === it.to
        || (it.to !== "/" && location.pathname.startsWith(it.to + "/"));
      if (!matches) continue;
      if (!bestItem || it.to.length > bestItem.to.length) {
        bestItem = it;
        bestGroup = g;
      }
    }
  }

  if (!bestGroup || bestGroup.items.length <= 1) return null;

  return (
    <div className="border-b bg-zinc-50/70 dark:bg-zinc-900/50 sticky top-14 z-10">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 h-9 flex items-center gap-1 text-sm overflow-x-auto">
        <span className="text-zinc-400 text-xs mr-2 shrink-0 select-none">{bestGroup.label}</span>
        {bestGroup.items.map((it) => {
          const active = it === bestItem;
          return (
            <Link
              key={it.to}
              to={it.to}
              className={`px-2 py-1 rounded text-xs whitespace-nowrap transition-colors ${
                active
                  ? "bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-white font-medium"
                  : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
            >
              {it.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function NavSubLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const isActive = location.pathname === item.to
    || (item.to !== "/" && location.pathname.startsWith(item.to + "/"));
  return (
    <Link
      to={item.to}
      className={`block px-3 py-1.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
        isActive ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200" : ""
      }`}
    >
      {item.label}
    </Link>
  );
}
